import { observable, resolve, singleton } from 'aurelia';
import {
  CalendarDay,
  DaySourceCount,
  FetchMode,
  MonthOption,
  SourceFilter,
  ToyRelease,
  isReleaseUpcoming
} from '../models';
import { HttpClient } from './http-client';
import { DiagnosticsService } from './diagnostics-service';

// Owns the calendar/releases domain: the month grid, the loaded month's releases, month
// navigation, the source/search filters, the selected-day releases panel, pagination,
// and the list/grid view toggle. Consumed by the calendar, filter, and releases components.
@singleton()
export class CalendarService {
  private readonly http: HttpClient = resolve(HttpClient);
  private readonly diagnostics: DiagnosticsService = resolve(DiagnosticsService);

  currentYear: number = 0;
  currentMonth: number = 0;
  monthName: string = '';
  // Short name of the month the in-flight fetch is loading. Distinct from monthName, which
  // tracks the month currently *rendered* (it is only updated by buildCalendar once the data
  // arrives, so the grid and the selected-day label stay consistent). Month navigation moves to
  // the target month before fetching, so the loading overlay must read this to name the month
  // being loaded rather than the one still on screen; for a same-month reload the two match.
  loadingMonthName: string = '';
  calendarDays: CalendarDay[] = [];
  loading: boolean = true;
  monthLoading: boolean = false;
  error: string | null = null;
  @observable selectedDay: CalendarDay | null = null;
  monthOptions: MonthOption[] = [];
  selectedMonthIndex: number = 0;
  releasesListEl: HTMLElement | undefined;
  lastUpdated: string | null = null;

  // Releases panel view mode: 'grid' (default compact cards) or 'list' (detailed rows).
  releasesView: 'list' | 'grid' = 'grid';
  // Whether the compact last-updated info popover is currently visible.
  updatedInfoOpen: boolean = false;
  // Whether the releases panel is expanded to fill the entire main layout (desktop affordance).
  releasesExpanded: boolean = false;

  // Mobile: the filter section collapses into a slide-in drawer toggled from the
  // page header. The releases drawer is driven by selectedDay.
  filterPanelOpen: boolean = false;

  // Filters
  sourceFilters: SourceFilter[] = [];
  @observable searchText: string = '';

  // Persisted enabled/disabled state for the source filters, keyed by source name. Loaded from
  // localStorage so a visitor's source selection survives a browser refresh; new sources not present
  // in the map default to active in rebuildAvailableSources.
  private static readonly SOURCE_FILTERS_KEY = 'toycalendar.sourceFilters';
  private persistedSourceState: Map<string, boolean> = CalendarService.loadPersistedSourceState();

  // Pagination for the releases panel. Server-paged: each page is fetched on demand from
  // /api/releases/day. The page resets to 1 whenever the selected day changes.
  static readonly PAGE_SIZE = 52;
  releasesPage: number = 1;
  releasesTotalCount: number = 0;
  releasesTotalPages: number = 1;
  dayLoading: boolean = false;

  // Per-(date, source) counts for the currently loaded month, from /api/releases/summary. The
  // calendar badges are derived by summing the counts of the currently active sources, so toggling
  // a source updates badges locally without refetching the month.
  private summaryCounts: DaySourceCount[] = [];

  private readonly months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Establishes the current month/year and month options. Called once on startup before the
  // first fetch so the header and navigation bounds are ready.
  initMonths(): void {
    const now = new Date();
    this.currentYear = now.getFullYear();
    this.currentMonth = now.getMonth();
    this.selectedMonthIndex = this.absoluteIndex(this.currentYear, this.currentMonth);
    this.buildMonthOptions();
  }

  // Number of months shown before / after the current month in the dropdown and navigation.
  private static readonly MONTHS_BEFORE = 1;
  private static readonly MONTHS_AFTER = 6;

  // Absolute month index (months since year 0) so ranges can cross year boundaries cleanly.
  private absoluteIndex(year: number, month: number): number {
    return year * 12 + month;
  }

  // The absolute index of the real current month, used as the anchor for the visible range.
  private get baseAbsoluteIndex(): number {
    const now = new Date();
    return this.absoluteIndex(now.getFullYear(), now.getMonth());
  }

  private get minAbsoluteIndex(): number {
    return this.baseAbsoluteIndex - CalendarService.MONTHS_BEFORE;
  }

  private get maxAbsoluteIndex(): number {
    return this.baseAbsoluteIndex + CalendarService.MONTHS_AFTER;
  }

  private buildMonthOptions(): void {
    this.monthOptions = [];
    for (let abs = this.minAbsoluteIndex; abs <= this.maxAbsoluteIndex; abs++) {
      const year = Math.floor(abs / 12);
      const month = abs % 12;
      this.monthOptions.push({ index: abs, label: `${this.months[month]} ${year}` });
    }
  }

  get canGoPrev(): boolean {
    return this.absoluteIndex(this.currentYear, this.currentMonth) > this.minAbsoluteIndex;
  }

  get canGoNext(): boolean {
    return this.absoluteIndex(this.currentYear, this.currentMonth) < this.maxAbsoluteIndex;
  }

  async prevMonth(): Promise<void> {
    if (!this.canGoPrev) return;
    this.setFromAbsoluteIndex(this.absoluteIndex(this.currentYear, this.currentMonth) - 1);
    this.selectedDay = null;
    await this.loadMonth();
  }

  async nextMonth(): Promise<void> {
    if (!this.canGoNext) return;
    this.setFromAbsoluteIndex(this.absoluteIndex(this.currentYear, this.currentMonth) + 1);
    this.selectedDay = null;
    await this.loadMonth();
  }

  async jumpToMonth(): Promise<void> {
    this.setFromAbsoluteIndex(Number(this.selectedMonthIndex));
    this.selectedDay = null;
    await this.loadMonth();
  }

  // Applies an absolute month index to the current year/month state and keeps the dropdown in sync.
  private setFromAbsoluteIndex(abs: number): void {
    this.currentYear = Math.floor(abs / 12);
    this.currentMonth = abs % 12;
    this.selectedMonthIndex = abs;
  }

  private async loadMonth(): Promise<void> {
    await this.fetchReleases('month');
  }

  // Local refresh: re-fetches the currently selected month straight from the database
  // (no source re-scrape). Uses the non-destructive 'month' mode so the grid stays visible.
  async reloadMonth(): Promise<void> {
    if (this.loading || this.monthLoading) return;
    await this.fetchReleases('month');
  }

  async fetchReleases(mode: FetchMode): Promise<void> {
    // 'initial'  → first load; shows the full-screen waiting message.
    // 'month'    → month navigation; keeps the current month visible until the new
    //              month's data arrives (non-destructive).
    if (mode === 'initial') {
      this.loading = true;
      this.error = null;
      this.selectedDay = null;
      this.diagnostics.clear();
    } else {
      this.monthLoading = true;
    }

    // Name the month this fetch targets. currentMonth has already been moved to the destination
    // by the navigation helpers, so this is the month the user is waiting on.
    this.loadingMonthName = this.months[this.currentMonth].slice(0, 3);

    // The month sent to the server is 1-based; currentMonth is 0-based.
    const year = this.currentYear;
    const month = this.currentMonth + 1;

    try {
      // Load the month summary: per-(date, source) counts plus source/brand lists and
      // last-updated. Calendar badges are derived from these counts client-side. The web app
      // is read-only; all scraping happens in the tray scraper.
      const summaryParams = new URLSearchParams({
        year: String(year),
        month: String(month),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      const search = this.searchText.trim();
      if (search) summaryParams.set('search', search);

      const response = await this.http.get(`/api/releases/summary?${summaryParams.toString()}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      this.summaryCounts = (data.counts || []) as DaySourceCount[];
      this.diagnostics.appendFetch(mode, year, month, data.diagnostics || []);
      this.error = null;
      this.lastUpdated = this.formatLastUpdated(data.lastUpdated);
      this.rebuildAvailableSources(data.sources || []);
      this.showMonth();
      // Re-fetch the open day (if any) so its list reflects the new month/search state.
      if (this.selectedDay) {
        await this.loadSelectedDay(1);
      }
    } catch (e: any) {
      if (mode === 'initial') {
        this.error = `Failed to load releases: ${e.message}`;
        this.summaryCounts = [];
        this.buildCalendar();
      } else {
        // Preserve the existing view; surface the error without wiping the screen.
        this.error = `Failed to load month: ${e.message}`;
      }
    } finally {
      this.loading = false;
      this.monthLoading = false;
    }
  }

  private showMonth(): void {
    // Badges are derived from the summary counts; source filtering is applied here (locally,
    // by summing only the active sources) while search is already applied server-side.
    this.buildCalendar();
  }

  // Sums the month summary counts per day-of-month across the currently active sources.
  private buildCountsByDay(): Map<number, number> {
    const activeSources = new Set(
      this.sourceFilters.filter(s => s.active).map(s => s.name)
    );
    const countsByDay = new Map<number, number>();
    for (const c of this.summaryCounts) {
      if (!activeSources.has(c.source)) continue;
      // The summary date is an ISO yyyy-MM-dd UTC date; take the day-of-month.
      const day = new Date(`${c.date}T00:00:00Z`).getUTCDate();
      countsByDay.set(day, (countsByDay.get(day) || 0) + c.count);
    }
    return countsByDay;
  }

  buildCalendar(): void {
    const year = this.currentYear;
    const month = this.currentMonth;

    this.monthName = this.months[month].slice(0, 3);

    // Remember which date was selected so the selection (right panel + highlight)
    // can be re-pointed to the rebuilt day object, e.g. after a refresh.
    const previousSelectedDate =
      this.selectedDay && this.selectedDay.isCurrentMonth ? this.selectedDay.date : null;

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();

    const countsByDay = this.buildCountsByDay();

    const days: CalendarDay[] = [];

    // Fill leading empty days (previous month)
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: prevMonthDays - i,
        isCurrentMonth: false,
        isToday: false,
        releaseCount: 0,
        releases: []
      });
    }

    // Fill current month days
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({
        date: d,
        isCurrentMonth: true,
        isToday: today.getFullYear() === year && today.getMonth() === month && today.getDate() === d,
        releaseCount: countsByDay.get(d) || 0,
        releases: []
      });
    }

    // Fill trailing days to complete the grid (6 rows of 7)
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push({
        date: d,
        isCurrentMonth: false,
        isToday: false,
        releaseCount: 0,
        releases: []
      });
    }

    this.calendarDays = days;

    // Re-point an existing selection to the rebuilt day object (preserving the
    // right panel across refreshes). Clear it if that day no longer has releases.
    if (previousSelectedDate !== null) {
      const rebuilt = days.find(d => d.isCurrentMonth && d.date === previousSelectedDate);
      if (rebuilt && rebuilt.releaseCount > 0) {
        // Carry over the already-loaded releases so the panel doesn't blank out.
        rebuilt.releases = this.selectedDay ? this.selectedDay.releases : [];
        this.selectedDay = rebuilt;
      } else {
        this.selectedDay = null;
      }
    }
  }

  // Selecting a day fetches the first page of that day's releases from the server. Clicking the
  // already-selected day toggles the panel closed.
  async selectDay(day: CalendarDay): Promise<void> {
    if (!day.isCurrentMonth || day.releaseCount <= 0) return;
    if (this.selectedDay === day) {
      this.selectedDay = null;
      return;
    }
    this.selectedDay = day;
    await this.loadSelectedDay(1);
  }

  // Fetches a single page of the selected day's releases from /api/releases/day, applying the
  // active source filter and current search server-side, and updates pagination state.
  private async loadSelectedDay(page: number): Promise<void> {
    const day = this.selectedDay;
    if (!day || !day.isCurrentMonth) return;

    const dateIso = this.toIsoDate(this.currentYear, this.currentMonth, day.date);
    const activeSources = this.sourceFilters.filter(s => s.active).map(s => s.name);

    const params = new URLSearchParams({
      date: dateIso,
      page: String(page),
      pageSize: String(CalendarService.PAGE_SIZE)
    });
    params.set('timeZone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (activeSources.length > 0) params.set('sources', activeSources.join(','));
    const search = this.searchText.trim();
    if (search) params.set('search', search);

    this.dayLoading = true;
    try {
      const response = await this.http.get(`/api/releases/day?${params.toString()}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const releases = (data.releases || []) as ToyRelease[];
      for (const r of releases) {
        // Resolve the release's absolute instant once, then derive both the displayed local
        // time and the availableAt epoch used to gate the timestamp/bell to future releases.
        const instant = this.resolveReleaseInstant(r.date, r.time);
        r.availableAt = instant ? instant.getTime() : null;
        r.localTime = instant
          ? instant.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
          : (r.time ?? null);
      }
      day.releases = releases;
      this.releasesPage = data.page || 1;
      this.releasesTotalCount = data.totalCount || 0;
      this.releasesTotalPages = Math.max(1, data.totalPages || 1);
      this.scrollReleasesToTop();
    } catch (e: any) {
      this.error = `Failed to load day: ${e.message}`;
    } finally {
      this.dayLoading = false;
    }
  }

  // Builds an ISO yyyy-MM-dd string for a 0-based month and day-of-month.
  private toIsoDate(year: number, month0: number, day: number): string {
    const mm = String(month0 + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  }

  setReleasesView(view: 'list' | 'grid'): void {
    if (this.releasesView === view) return;
    this.releasesView = view;
    this.scrollReleasesToTop();
  }

  // Expand the releases panel to fill the whole layout (hiding the filter and calendar), or
  // collapse it back to the normal three-column grid.
  toggleReleasesExpanded(): void {
    this.releasesExpanded = !this.releasesExpanded;
  }

  toggleUpdatedInfo(): void {
    this.updatedInfoOpen = !this.updatedInfoOpen;
  }

  closeUpdatedInfo(): void {
    this.updatedInfoOpen = false;
  }

  toggleFilterPanel(): void {
    this.filterPanelOpen = !this.filterPanelOpen;
  }

  closeFilterPanel(): void {
    this.filterPanelOpen = false;
  }

  // Mobile: the releases drawer is driven by the day selection, so closing it
  // just clears the selected day.
  closeReleasesPanel(): void {
    this.selectedDay = null;
    this.releasesExpanded = false;
  }

  // Reset to the first page whenever the selected day changes (Aurelia @observable hook).
  selectedDayChanged(): void {
    this.releasesPage = 1;
    this.releasesTotalCount = 0;
    this.releasesTotalPages = 1;
  }

  // Full label for the selected day including the navigated year, since the month range can
  // now span into the next year and "January 15" alone would be ambiguous.
  get selectedDateLabel(): string {
    if (!this.selectedDay) return '';
    return `${this.monthName} ${this.selectedDay.date}`;
  }

  get totalPages(): number {
    return this.releasesTotalPages;
  }

  get isPaginated(): boolean {
    return this.releasesTotalCount > CalendarService.PAGE_SIZE;
  }

  // The releases currently rendered are exactly the server-returned page for the selected day.
  get pagedReleases(): ToyRelease[] {
    return this.selectedDay ? this.selectedDay.releases : [];
  }

  get releaseGroups(): { header: string; releases: ToyRelease[] }[] {
    const releases = this.pagedReleases;
    const groups: { header: string; releases: ToyRelease[] }[] = [];
    for (const release of releases) {
      const header = isReleaseUpcoming(release)
        ? 'Upcoming'
        : this.formatFirstSeenHour(release.firstSeenAt);
      const group = groups[groups.length - 1];
      if (group?.header === header) {
        group.releases.push(release);
      } else {
        groups.push({ header, releases: [release] });
      }
    }
    return groups;
  }

  private formatFirstSeenHour(firstSeenAt: string | null | undefined): string {
    if (!firstSeenAt) return 'Unknown';
    const date = new Date(firstSeenAt);
    if (isNaN(date.getTime())) return 'Unknown';
    date.setMinutes(0, 0, 0);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  // Human-friendly "1–52 of 137" range label for the current page.
  get pageRangeLabel(): string {
    if (!this.selectedDay || this.releasesTotalCount === 0) return '';
    const start = (this.releasesPage - 1) * CalendarService.PAGE_SIZE + 1;
    const end = Math.min(this.releasesPage * CalendarService.PAGE_SIZE, this.releasesTotalCount);
    return `${start}\u2013${end} of ${this.releasesTotalCount}`;
  }

  async prevReleasesPage(): Promise<void> {
    if (this.releasesPage > 1) {
      await this.loadSelectedDay(this.releasesPage - 1);
    }
  }

  async nextReleasesPage(): Promise<void> {
    if (this.releasesPage < this.totalPages) {
      await this.loadSelectedDay(this.releasesPage + 1);
    }
  }

  async firstReleasesPage(): Promise<void> {
    if (this.releasesPage > 1) {
      await this.loadSelectedDay(1);
    }
  }

  async lastReleasesPage(): Promise<void> {
    if (this.releasesPage < this.totalPages) {
      await this.loadSelectedDay(this.totalPages);
    }
  }

  private scrollReleasesToTop(): void {
    // Defer until Aurelia has rendered the new page so the reset lands on the
    // updated list height.
    requestAnimationFrame(() => {
      if (this.releasesListEl) {
        this.releasesListEl.scrollTop = 0;
      }
    });
  }

  toggleSource(source: SourceFilter, event?: Event): void {
    // The checkbox two-way binding already set source.active; when invoked from the
    // change event, trust the DOM's checked state so we don't cancel that update.
    if (event) {
      source.active = (event.target as HTMLInputElement).checked;
    } else {
      source.active = !source.active;
    }
    this.persistSourceState();
    this.applySourceChange();
  }

  selectAllSources(): void {
    for (const s of this.sourceFilters) {
      s.active = true;
    }
    this.persistSourceState();
    this.applySourceChange();
  }

  clearAllSources(): void {
    for (const s of this.sourceFilters) {
      s.active = false;
    }
    this.persistSourceState();
    this.applySourceChange();
  }

  // A source-filter change only affects badge counts (summed locally) and the currently open
  // day's server-side filtered page; the month summary itself does not need to be refetched.
  private applySourceChange(): void {
    this.showMonth();
    if (this.selectedDay) {
      // The rebuilt selection may have been cleared if the day lost all its releases.
      if (this.selectedDay) {
        void this.loadSelectedDay(1);
      }
    }
  }

  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  searchTextChanged(): void {
    // Search now runs server-side (it affects both the summary counts and the day pages), so
    // debounce to avoid a request per keystroke.
    if (this.searchDebounceHandle !== null) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      this.searchDebounceHandle = null;
      void this.fetchReleases('month');
    }, 300);
  }

  clearSearch(): void {
    this.searchText = '';
  }

  // Reapplies a saved search's filter selection: restores the search text and marks only
  // the saved source names active. Names no longer available are simply ignored.
  applyFilterSnapshot(searchText: string, sources: string[]): void {
    const savedSources = new Set(sources);
    for (const s of this.sourceFilters) {
      s.active = savedSources.has(s.name);
    }
    this.persistSourceState();
    this.searchText = searchText;
    this.selectedDay = null;
    // Search and sources may both have changed; refetch the summary (search is server-side).
    if (this.searchDebounceHandle !== null) {
      clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = null;
    }
    void this.fetchReleases('month');
  }

  // The currently active source names, used when capturing a saved search.
  activeSourceNames(): string[] {
    return this.sourceFilters.filter(s => s.active).map(s => s.name);
  }

  private rebuildAvailableSources(sources: string[]): void {
    const existing = new Map(this.sourceFilters.map(s => [s.name, s.active]));
    // Preserve current active state; fall back to the persisted (cross-refresh) selection, and
    // default brand-new sources to active.
    this.sourceFilters = [...sources].sort().map(name => ({
      name,
      active: existing.get(name) ?? this.persistedSourceState.get(name) ?? true
    }));
    this.persistSourceState();
  }

  // Loads the persisted per-source enabled/disabled map from localStorage, tolerating missing or
  // malformed data by returning an empty map (all sources then default to active).
  private static loadPersistedSourceState(): Map<string, boolean> {
    try {
      const raw = localStorage.getItem(CalendarService.SOURCE_FILTERS_KEY);
      if (!raw) return new Map();
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return new Map();
      const map = new Map<string, boolean>();
      for (const [name, active] of Object.entries(parsed)) {
        if (typeof active === 'boolean') map.set(name, active);
      }
      return map;
    } catch {
      // Corrupt or unavailable storage: start with no persisted selection.
      return new Map();
    }
  }

  // Persists the current source filter selection to localStorage, keyed by source name, so it
  // survives a browser refresh.
  private persistSourceState(): void {
    for (const s of this.sourceFilters) {
      this.persistedSourceState.set(s.name, s.active);
    }
    try {
      const record: Record<string, boolean> = {};
      for (const [name, active] of this.persistedSourceState) {
        record[name] = active;
      }
      localStorage.setItem(CalendarService.SOURCE_FILTERS_KEY, JSON.stringify(record));
    } catch {
      // Storage may be full or unavailable (e.g. private mode); ignore silently.
    }
  }

  // Formats the server's UTC "last updated" timestamp into a short local string, e.g.
  // "Jul 14, 2025, 9:03 AM". Returns null when there is no timestamp (empty database).
  private formatLastUpdated(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString([], {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  }

  private parseTimeForSort(timeStr: string | null): number {
    if (!timeStr) return 9999;
    // "Just Dropped" or other non-time labels sort last.
    // Minutes are optional (e.g. "9am PT").
    const match = timeStr.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
    if (!match) return 9998;
    if (match[2] === undefined && match[3] === undefined) return 9998;
    let hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    const ampm = match[3]?.toUpperCase();
    if (ampm === 'PM' && hours !== 12) hours += 12;
    if (ampm === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  }

  // Resolves a release's date + raw time string into an absolute instant. Returns null when the
  // time is missing or is a non-time label (e.g. "Just Dropped"), so callers can distinguish a
  // concrete availability moment from a display-only label. Shared by localTime formatting and
  // the availableAt gate.
  private resolveReleaseInstant(dateStr: string, time: string | null): Date | null {
    if (!time) return null;

    // Times come in formats like "9:00 AM PT", "12:00 PM ET", "9am PT", "10 AM CT",
    // or an absolute "4:00 PM UTC" (e.g. Mattel Creations Featured).
    // Minutes are optional (e.g. Mattel Creations reports "9am PT").
    const match = time.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?\s*(UTC|GMT|PT|PST|PDT|ET|EST|EDT|CT|CST|CDT|MT|MST|MDT)?$/i);
    if (!match) return null;

    // Require either minutes or an AM/PM marker to treat this as a real time,
    // so labels like "Just Dropped" are not mistaken for a concrete instant.
    if (match[2] === undefined && match[3] === undefined) return null;

    let hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    const ampm = match[3]?.toUpperCase();
    const tz = match[4]?.toUpperCase();

    // Convert 12-hour to 24-hour
    if (ampm === 'PM' && hours !== 12) hours += 12;
    if (ampm === 'AM' && hours === 12) hours = 0;

    // Timezone abbreviations to IANA zone IDs. All variants of a region (PT/PST/PDT) map to the
    // same zone and the actual offset is resolved for the release date, so DST is handled correctly
    // year-round and sources that write "PST" in summer (or "PT" in winter) still resolve right.
    // Mirrors the server's ParseReleaseInstant.
    const tzIds: Record<string, string> = {
      'UTC': 'UTC', 'GMT': 'UTC',
      'PT': 'America/Los_Angeles', 'PST': 'America/Los_Angeles', 'PDT': 'America/Los_Angeles',
      'ET': 'America/New_York', 'EST': 'America/New_York', 'EDT': 'America/New_York',
      'CT': 'America/Chicago', 'CST': 'America/Chicago', 'CDT': 'America/Chicago',
      'MT': 'America/Denver', 'MST': 'America/Denver', 'MDT': 'America/Denver',
    };

    // Start from the wall-clock time read as UTC, then shift by the zone's offset on that date.
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    d.setUTCHours(hours, minutes, 0, 0);

    const zoneId = tz ? tzIds[tz] : undefined;
    if (!zoneId || zoneId === 'UTC') return d;

    const offsetMs = CalendarService.zoneOffsetMs(d, zoneId);
    if (offsetMs === null) return d;

    // The offset may differ across a DST transition; re-evaluate once at the adjusted instant.
    let instant = new Date(d.getTime() - offsetMs);
    const offsetMs2 = CalendarService.zoneOffsetMs(instant, zoneId);
    if (offsetMs2 !== null && offsetMs2 !== offsetMs) {
      instant = new Date(d.getTime() - offsetMs2);
    }
    return instant;
  }

  // Returns the UTC offset (ms) of `zoneId` at `instant`, or null if the zone is unsupported.
  private static readonly zoneFormatters = new Map<string, Intl.DateTimeFormat | null>();

  private static zoneOffsetMs(instant: Date, zoneId: string): number | null {
    let fmt = CalendarService.zoneFormatters.get(zoneId);
    if (fmt === undefined) {
      try {
        fmt = new Intl.DateTimeFormat('en-US', {
          timeZone: zoneId, hourCycle: 'h23',
          year: 'numeric', month: 'numeric', day: 'numeric',
          hour: 'numeric', minute: 'numeric', second: 'numeric',
        });
      } catch {
        fmt = null;
      }
      CalendarService.zoneFormatters.set(zoneId, fmt);
    }
    if (!fmt) return null;

    const parts: Record<string, number> = {};
    for (const p of fmt.formatToParts(instant)) {
      if (p.type !== 'literal') parts[p.type] = parseInt(p.value, 10);
    }
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    // Drop sub-second noise so the comparison is exact.
    return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
  }
}

// Matches a lowercased haystack against a lowercased query using an ordered token match: the
// query is split on whitespace and each token must appear in sequence, like a SQL `%a%b%c%`
// LIKE. This loosens multi-word searches — "mafex spider" matches "MAFEX No.185 Spider-Man" —
// without being as permissive as an unordered match. An all-whitespace query matches everything.
// Kept identical to the server's SavedSearchNotificationService.MatchesSearchTokens.
export function matchesSearchTokens(haystackLower: string, queryLower: string): boolean {
  const tokens = queryLower.split(/\s+/).filter(t => t.length > 0);
  if (tokens.length === 0) return true;
  let from = 0;
  for (const token of tokens) {
    const idx = haystackLower.indexOf(token, from);
    if (idx < 0) return false;
    from = idx + token.length;
  }
  return true;
}
